<template>
  <div class="workflow-list-container">
    <PersonalNotesModal
      v-if="showPersonalNotesModal && personalNotesModalWorkflow"
      :workflowId="personalNotesModalWorkflow.id"
      :value="personalNotesModalWorkflow.LatestUserComment"
      @closeModal="closePersonalNotesModal"
    />
    <div v-if="workflows.length == 0 && !loading" class="no-results">
      <span>No results found</span>
    </div>
    <ListView
      class="workflow-list"
      :data-items="workflows"
      item="workflowTile"
      footer="workflowListFooter"
      @scroll="scrollHandler($event as unknown as Event)"
      data-cy="workflow-list"
    >
      <template v-slot:workflowTile="{ props }">
        <WorkflowTile
          class="workflow-list__tile"
          :workflow="props.dataItem"
          @urgentClicked="onUrgentClicked"
          @privateNotesClicked="onPrivateNotesClicked"
        />
      </template>

      <template v-slot:workflowListFooter>
        <Loader
          v-show="loading"
          class="workflow-loader"
          size="large"
          type="pulsing"
          data-cy="workflow-loader"
        />
      </template>
    </ListView>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { ListView } from "@progress/kendo-vue-listview";
import { Loader } from "@progress/kendo-vue-indicators";
import WorkflowTile from "./WorkflowTile.vue";
import type { WfSubmissionProductAndIngredient } from "@/Domain/WfSubmissionProductAndIngredient";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { api } from "@/api";
import PersonalNotesModal from "./PersonalNotesModal.vue";

const props = defineProps<{
  workflows: WfSubmissionProductAndIngredient[];
  totalWorkflows: number;
  defaultTake?: number;
  skip: number;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: "workflowsChanged", skip: number, take: number): void;
}>();

const main = useMainStore();
const notificationStore = useNotificationStore();

const take = ref<number>(props.defaultTake ?? 10);
const skip = ref<number>(props.skip);
const showPersonalNotesModal = ref(false);
const personalNotesModalWorkflow = ref<WfSubmissionProductAndIngredient | null>(
  null
);

const loadScrollThreshold = 100;

const scrollHandler = function (e: Event) {
  const target = e.target as HTMLDivElement | null;
  if (!target) return;
  if (
    target.scrollTop + loadScrollThreshold <
    target.scrollHeight - target.clientHeight
  )
    return;
  if (skip.value + take.value >= props.totalWorkflows) return;
  if (props.loading) return;

  skip.value = skip.value + take.value;
  emit("workflowsChanged", skip.value, take.value);
};

const onPrivateNotesClicked = function (wf: WfSubmissionProductAndIngredient) {
  personalNotesModalWorkflow.value = wf;
  showPersonalNotesModal.value = true;
};

const closePersonalNotesModal = function (updated: boolean, newNotes: string) {
  if (updated) {
    personalNotesModalWorkflow.value!.LatestUserComment = newNotes;
  }
  personalNotesModalWorkflow.value = null;
  showPersonalNotesModal.value = false;
};

const onUrgentClicked = function (dataItem: WfSubmissionProductAndIngredient) {
  const wf = dataItem as WfSubmissionProductAndIngredient;

  const errorNotification = {
    message: `Error marking Job ${wf.JobId} as ${
      wf.IsUrgent ? "not urgent" : "urgent"
    }. Please try again`,
    type: NotificationTypeEnum.error,
    duration: 5000,
  };

  handleResponsePromiseAsync(
    api.get("SetWorkflowUrgentStatus", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      workflowId: wf.id,
      isUrgent: !wf.IsUrgent,
    }),
    (data) => {
      if (data) {
        wf.IsUrgent = !wf.IsUrgent;
        notificationStore.notify({
          message: `Job ${wf.JobId} marked as ${
            wf.IsUrgent ? "urgent" : "not urgent"
          } `,
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      } else {
        notificationStore.notify(errorNotification);
      }
    },
    "WorkflowList.vue",
    "onUrgentClick",
    () => {
      notificationStore.notify(errorNotification);
    }
  );
};

watchEffect(() => {
  skip.value = props.skip;
});
</script>

<style lang="scss">
.workflow-list {
  background: none;
  border: none;
  margin: 16px 0;
  max-height: 70vh;

  .k-listview-content {
    padding: 15px 15px;
  }

  .k-listview-footer {
    border-top-width: 0;
  }

  &__tile {
    margin-bottom: 16px;
  }
}
.workflow-loader {
  display: flex;
  justify-content: center;
  margin: 32px 0;
}

.no-results {
  display: flex;
  justify-content: center;
  margin: 32px 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: #6c757d;
}
</style>
